import React, { useMemo, useState } from 'react';
import { Newspaper } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, EmptyState, ErrorState, LoadingState } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { adminApi, formatDate, INDUSTRIES, plural, toastError, useAdminQuery, useBusy } from './adminApi';
import { AdminSection, FilterChips, Pager, RefreshError, SearchField, Toolbar } from './AdminUi';

const PAGE = 50;
const MODES = [
  { value: 'set', label: 'Set to' },
  { value: 'add', label: 'Add' },
  { value: 'remove', label: 'Remove' },
];

const isEveryone = (i) => !i.target_industries || i.target_industries.length === 0 || i.target_industries.includes('Global');

/**
 * Which industries see which published headline. Select rows (or every
 * match of the current search) and apply one change to all of them.
 */
export default function InsightsTab() {
  const { data, error, loading, reload, setData } = useAdminQuery(() => adminApi.get('/admin/insights').then((r) => r.data), []);
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [mode, setMode] = useState('set');
  const [industries, setIndustries] = useState([]);
  const { run, busy } = useBusy();

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data || []).filter((i) => !needle || `${i.title} ${i.source}`.toLowerCase().includes(needle));
  }, [data, q]);
  const page = rows.slice(offset, offset + PAGE);
  const pageIds = page.map((i) => i.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSelected((s) => {
    const n = new Set(s);
    pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id)));
    return n;
  });
  const toggleIndustry = (name) => setIndustries((l) => (l.includes(name) ? l.filter((x) => x !== name) : [...l, name]));

  const targetIds = selected.size ? [...selected] : [];
  const apply = (ids) => run('apply', async () => {
    try {
      await adminApi.post('/admin/insights/targeting/bulk', { insight_ids: ids, mode, target_industries: industries });
      toast.success(`Targeting updated on ${plural(ids.length, 'insight')}`);
      setSelected(new Set());
      await reload();
    } catch (err) {
      toastError(err, 'Could not update targeting');
    }
  });

  const canApply = mode === 'set' || industries.length > 0;

  return (
    <AdminSection
      title="Insights targeting"
      count={data ? data.length : null}
      description="Headlines with no targeting are shown to every customer. Targeting one or more industries limits who sees them."
    >
      <Toolbar>
        <SearchField value={q} onChange={(v) => { setQ(v); setOffset(0); }} label="Search insights" placeholder="Search headline or source" />
      </Toolbar>

      <div className="adm-inline-form" role="group" aria-label="Bulk targeting">
        <div className="adm-toolbar">
          <FilterChips label="Change" options={MODES} value={mode} onChange={setMode} testIdPrefix="targeting-mode" />
          <div className="adm-chips" role="group" aria-label="Industries">
            {INDUSTRIES.map((name) => (
              <button key={name} type="button" className="adm-chip" aria-pressed={industries.includes(name)} onClick={() => toggleIndustry(name)}>{name}</button>
            ))}
          </div>
        </div>
        <p className="adm-hint">
          {mode === 'set' && industries.length === 0 ? 'Setting no industries shows the selected insights to everyone.' : `${MODES.find((m) => m.value === mode).label} ${industries.join(', ') || 'no industries'}.`}
        </p>
        <div className="adm-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button size="sm" disabled={!targetIds.length || !canApply || busy('apply')} onClick={() => apply(targetIds)} data-testid="targeting-apply-selected">
            Apply to {plural(targetIds.length, 'selected insight')}
          </Button>
          <Button size="sm" variant="outline" disabled={!rows.length || !canApply || busy('apply')} onClick={() => apply(rows.map((i) => i.id))}>
            Apply to all {rows.length} matching
          </Button>
        </div>
      </div>

      {loading ? (
        <LoadingState label="insights" rows={6} />
      ) : error && !data ? (
        <ErrorState title="Couldn't load insights" error={error} onRetry={reload} />
      ) : !data.length ? (
        <EmptyState icon={Newspaper} title="No insights collected yet" description="Headlines appear here as feeds are ingested." />
      ) : !rows.length ? (
        <EmptyState variant="compact" title="No matching insights" description={`Nothing matched "${q}".`} />
      ) : (
        <>
          <RefreshError error={error} onRetry={reload} what="insights" />
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--stack" data-testid="insights-table">
              <thead>
                <tr>
                  <th scope="col" className="adm-col-check">
                    <input type="checkbox" className="adm-checkbox" checked={allOnPage} onChange={togglePage} aria-label="Select all insights on this page" />
                  </th>
                  <th scope="col">Headline</th>
                  <th scope="col">Published</th>
                  <th scope="col">Shown to</th>
                </tr>
              </thead>
              <tbody>
                {page.map((i) => (
                  <tr key={i.id}>
                    <td className="adm-col-check">
                      <input type="checkbox" className="adm-checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} aria-label={`Select ${i.title}`} />
                    </td>
                    <td data-label="Headline" className="adm-break">
                      <div className="adm-strong">{i.title}</div>
                      {i.evidence_status === 'unverified_social_claim' && <Badge size="sm" tone="warning">Unverified social report</Badge>}
                      <div className="adm-meta">{(() => { try { return new URL(i.url).hostname; } catch { return i.source; } })()}</div>
                    </td>
                    <td data-label="Published" className="adm-mono adm-nowrap adm-secondary">{formatDate(i.published_at)}</td>
                    <td data-label="Shown to">
                      {isEveryone(i) ? <Badge size="sm" plain>Everyone</Badge> : (
                        <span className="adm-chips">{i.target_industries.map((t) => <Badge key={t} size="sm" tone="accent" plain>{t}</Badge>)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={PAGE} total={rows.length} onChange={setOffset} label="Insights pagination" />
        </>
      )}
    </AdminSection>
  );
}
